
import { defineComponent, PropType, computed, reactive, onBeforeMount } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// types
import {
    CarCapacity,
    CostStandards,
    CostStandardsPayload,
    CostStandardsUpdatePayload,
} from '@/new-services/types/tms'
import { CityT } from '@/core/types/cities.types'

// realisations
import { cities } from '@/core/realisations/cities.realisation'

// services
import tmsAPI from '@/new-services/tms.service'

// composable
import useNotifications from '@/common/composable/useNotifications'

// validations
import useVuelidate from '@vuelidate/core'
import { decimal, required } from '@vuelidate/validators'

const TRANSPORTATION_TYPES = [
    { id: 1, name: 'LTL' },
    { id: 2, name: 'FTL' },
]

// const CAR_CAPACITIES = [
//     { id: 1, name: '1.5 тонны' },
//     { id: 2, name: '3 тонны' },
//     { id: 3, name: '5 тонн' },
//     { id: 4, name: '7 тонн' },
//     { id: 5, name: '10 тонн' },
//     { id: 6, name: '20 тонн' },
//     { id: 7, name: 'Консол' }
// ]

export default defineComponent({
    components: {
        Modal,
        TheSelect,
        TheButton,
        TheInput,
    },
    props: {
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<CostStandards>,
            default: null,
        },
    },
    emits: ['close', 'created', 'updated'],
    setup(props, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            //
            sender_city: null as number | null,
            receiver_city: null as number | null,
            transit_cities: [] as Array<number | null>,
            costPerWeight: null as string | null,
            invoiceType: null as number | null,
            //
            capacity_1500: null as string | number | null,
            capacity_3000: null as string | number | null,
            capacity_5000: null as string | number | null,
            capacity_10000: null as string | number | null,
            capacity_20000: null as string | number | null,
        })

        const state_form$ = useVuelidate(
            {
                sender_city: { required },
                receiver_city: { required },
                invoiceType: { required },
                costPerWeight: { decimal },
                capacity_1500: { decimal },
                capacity_3000: { decimal },
                capacity_5000: { decimal },
                capacity_10000: { decimal },
                capacity_20000: { decimal },
            },
            state
        )

        const cities_state = reactive({
            is_loading: false,
            is_failed: false,
            list: [] as CityT[],
        })

        const loadCities = async () => {
            try {
                cities_state.is_loading = true

                const response = await cities.getAllList(1, undefined, 9999)
                cities_state.list = response.data

                cities_state.is_failed = false
            } catch (error) {
                console.error(error)
                cities_state.is_failed = true
                notifications.notifyError({
                    error,
                    title: 'При получении списка городов произошла ошибка',
                })
            } finally {
                cities_state.is_loading = false
            }
        }

        const addTransitCity = () => {
            state.transit_cities.push(null)
        }

        const deleteTransitCity = (transit_city_index: number) => {
            const answer = confirm(
                `Вы действительно хотите удалить транзитный город №${transit_city_index + 1}?`
            )
            if (!answer) return

            state.transit_cities.splice(transit_city_index, 1)
        }

        const onCancel = () => {
            if (state.is_loading) return
            emit('close')
        }

        const createCostStandards = async () => {
            const cityIds = [...state.transit_cities]
            cityIds.unshift(state.sender_city)
            cityIds.push(state.receiver_city)

            const capacities = []

            if (Number(state.capacity_1500)) {
                capacities.push({ id: 1, cost: Number(state.capacity_1500) })
            }

            if (Number(state.capacity_3000)) {
                capacities.push({ id: 2, cost: Number(state.capacity_3000) })
            }

            if (Number(state.capacity_5000)) {
                capacities.push({ id: 3, cost: Number(state.capacity_5000) })
            }

            if (Number(state.capacity_10000)) {
                capacities.push({ id: 5, cost: Number(state.capacity_10000) })
            }

            if (Number(state.capacity_20000)) {
                capacities.push({ id: 6, cost: Number(state.capacity_20000) })
            }

            try {
                state.is_loading = true

                const payload: CostStandardsPayload = {
                    cityIds: cityIds.filter((cityId) => cityId !== null) as number[],
                    invoiceType: state.invoiceType as number,
                    costPerWeight: Number(state.costPerWeight),
                    capacities,
                }

                await tmsAPI.createCostStandards(payload)

                emit('created')

                notifications.notify({
                    type: 'success',
                    title: 'Нормы себестоимости созданы',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При создании норм себестоимости произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const updateCostStandards = async () => {
            const cityIds = [...state.transit_cities]
            cityIds.unshift(state.sender_city)
            cityIds.push(state.receiver_city)

            const capacities = []

            if (Number(state.capacity_1500)) {
                capacities.push({ id: 1, cost: Number(state.capacity_1500) })
            }

            if (Number(state.capacity_3000)) {
                capacities.push({ id: 2, cost: Number(state.capacity_3000) })
            }

            if (Number(state.capacity_5000)) {
                capacities.push({ id: 3, cost: Number(state.capacity_5000) })
            }

            if (Number(state.capacity_10000)) {
                capacities.push({ id: 5, cost: Number(state.capacity_10000) })
            }

            if (Number(state.capacity_20000)) {
                capacities.push({ id: 6, cost: Number(state.capacity_20000) })
            }

            try {
                state.is_loading = true

                const payload: CostStandardsUpdatePayload = {
                    ids: props.initialValue.ids,
                    cityIds: cityIds.filter((cityId) => cityId !== null) as number[],
                    invoiceType: state.invoiceType as number,
                    costPerWeight: Number(state.costPerWeight),
                    capacities,
                }

                await tmsAPI.updateCostStandards(payload)

                emit('updated')

                notifications.notify({
                    type: 'success',
                    title: 'Нормы себестоимости сохранены',
                })
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении норм себестоимости произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const onConfirm = () => {
            if (state.is_loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (props.isCreate) {
                createCostStandards()
                return
            }

            if (props.isUpdate) {
                updateCostStandards()
                return
            }
        }

        onBeforeMount(async () => {
            await loadCities()

            if (props.isUpdate && props.initialValue) {
                const cityIds = [...props.initialValue.cityIds]
                state.costPerWeight = props.initialValue.cost_per_weight

                state.sender_city = cityIds[0]
                state.receiver_city = cityIds[cityIds.length - 1]

                cityIds.splice(0, 1)
                cityIds.splice(cityIds.length - 1, 1)

                if (cityIds.length) {
                    state.transit_cities = cityIds
                }

                const invoiceType = TRANSPORTATION_TYPES.find(
                    (transporation_type) =>
                        transporation_type.name.toUpperCase() ===
                        props.initialValue.invoiceType.toUpperCase()
                )
                if (invoiceType) {
                    state.invoiceType = invoiceType.id

                    if (props.initialValue.capacities.length) {
                        props.initialValue.capacities.forEach((capacity) => {
                            switch (capacity.id) {
                                case 1:
                                    state.capacity_1500 = capacity.price
                                    break

                                case 2:
                                    state.capacity_3000 = capacity.price
                                    break

                                case 3:
                                    state.capacity_5000 = capacity.price
                                    break

                                case 5:
                                    state.capacity_10000 = capacity.price
                                    break

                                case 6:
                                    state.capacity_20000 = capacity.price
                                    break
                            }
                        })
                    }
                }
            }
        })

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Создать'
            if (props.isUpdate) return 'Редактировать'
            return null
        })

        const confirmButtonText = computed(() => {
            if (props.isCreate) return 'Создать'
            if (props.isUpdate) return 'Сохранить'
            return 'Подтвердить'
        })

        return {
            state,
            state_form$,
            cities_state,
            loadCities,
            addTransitCity,
            deleteTransitCity,
            onCancel,
            modalTitle,
            confirmButtonText,
            onConfirm,
            TRANSPORTATION_TYPES,
        }
    },
})
